/**
 * Studio in-place size switch (Phase 15.6): confirm → new project (+ optional design migrate).
 */

/**
 * @param {HTMLElement} root
 * @param {{ getState: () => Record<string, unknown>, subscribe: (fn: (state: Record<string, unknown>) => void) => void }} store
 * @param {{
 *   changeSize: (token: string, body: { template_id: string, migrate_designs: boolean }) => Promise<Record<string, unknown>>,
 *   withBusy: (action: () => Promise<void>, meta?: { title?: string, detail?: string }) => Promise<void>,
 *   confirm: (options: { title?: string, message?: string, confirmLabel?: string, cancelLabel?: string }) => Promise<boolean>,
 *   onSizeChanged?: (payload: Record<string, unknown>) => Promise<void>|void
 * }} deps
 */
export function bindChangeSizePanel(root, store, deps) {
    const toggle = root.querySelector('[data-bag-change-size-toggle]');
    const panel = root.querySelector('[data-bag-change-size-panel]');
    const statusEl = root.querySelector('#bag-change-size-status');
    let confirmOpen = false;

    if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
        return;
    }

    /**
     * @param {string} message
     * @param {boolean} [isError]
     */
    function setStatus(message, isError) {
        if (!statusEl) {
            return;
        }
        statusEl.textContent = message || '';
        statusEl.classList.toggle('text-danger', Boolean(isError));
        statusEl.classList.toggle('text-muted', !isError);
    }

    function setOpen(open) {
        panel.classList.toggle('d-none', !open);
        if (open) {
            panel.removeAttribute('hidden');
        } else {
            panel.setAttribute('hidden', '');
        }
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    toggle.addEventListener('click', () => {
        const open = panel.classList.contains('d-none') || panel.hasAttribute('hidden');
        setOpen(open);
        if (open) {
            setStatus('');
        }
    });

    root.querySelectorAll('[data-bag-size-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (!(btn instanceof HTMLButtonElement) || btn.disabled || confirmOpen) {
                return;
            }

            const nextId = (btn.getAttribute('data-template-id') || '').trim();
            const label = (btn.getAttribute('data-size-label') || nextId).trim();
            const currentId = String(store.getState().templateId || '').trim();
            if (!nextId || nextId === currentId) {
                return;
            }

            const designs = Array.isArray(store.getState().designs) ? store.getState().designs : [];
            const hasSelectedOk = designs.some(
                (d) => d && d.is_selected && d.generation_status === 'ok',
            );
            const confirmMsg = hasSelectedOk
                ? `Switch to ${label}? This starts a new tote. We’ll re-fit your current front/back designs to the new print area when possible.`
                : `Switch to ${label}? This starts a new tote on that size.`;

            confirmOpen = true;
            deps.confirm({
                title: 'Change tote size?',
                message: confirmMsg,
                confirmLabel: 'Switch size',
                cancelLabel: 'Cancel',
            })
                .then((ok) => {
                    if (!ok) {
                        return;
                    }

                    const token = String(store.getState().projectToken || '').trim();
                    if (!token) {
                        setStatus('Studio is still preparing your tote. Please wait a moment and try again.', true);
                        return;
                    }

                    return deps.withBusy(async () => {
                        setStatus('Switching size…');
                        try {
                            const payload = await deps.changeSize(token, {
                                template_id: nextId,
                                migrate_designs: true,
                            });
                            const redirect = typeof payload.redirect_url === 'string'
                                ? payload.redirect_url.trim()
                                : '';
                            const canStay = typeof deps.onSizeChanged === 'function'
                                && payload.template
                                && typeof payload.template === 'object';
                            if (canStay) {
                                setStatus('Fitting the new size…');
                                await deps.onSizeChanged(payload);
                                setStatus('Size updated.');
                                return;
                            }
                            if (!redirect) {
                                throw new Error('Missing redirect after size change.');
                            }
                            setStatus('Opening the new size…');
                            window.location.assign(redirect);
                        } catch (err) {
                            setStatus(
                                err && err.message ? err.message : 'Could not change size. Please try again.',
                                true,
                            );
                        }
                    }, {
                        title: 'Switching size…',
                        detail: 'Opening a new tote on the size you picked.',
                    });
                })
                .catch((err) => {
                    setStatus(
                        err && err.message ? err.message : 'Could not change size. Please try again.',
                        true,
                    );
                })
                .finally(() => {
                    confirmOpen = false;
                });
        });
    });

    store.subscribe((state) => {
        const currentId = String(state.templateId || '').trim();
        const locked = Boolean(state.busy);
        root.querySelectorAll('[data-bag-size-option]').forEach((btn) => {
            if (!(btn instanceof HTMLButtonElement)) {
                return;
            }
            const optionId = (btn.getAttribute('data-template-id') || '').trim();
            const current = optionId === currentId;
            btn.disabled = locked || current;
            btn.classList.toggle('is-active', current);
            btn.setAttribute('aria-pressed', current ? 'true' : 'false');
        });
        if (toggle instanceof HTMLButtonElement) {
            toggle.disabled = locked;
        }
    });
}
